import { create } from "zustand";

import { User, UserRole } from "../types/user";
import { decodeJwtToken, isTokenExpired } from "../utils/jwtUtils";

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
  hasRole: (role: string) => boolean;
  logout: () => Promise<void>;
  setUser: (user: User | null) => void;
  setToken: (token: string | null, persist?: boolean) => void;
  setRefreshToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  initializeAuth: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: localStorage.getItem("token") || sessionStorage.getItem("token"),
  refreshToken: localStorage.getItem("refreshToken"),
  loading: true,
  error: null,

  setUser: (user: User | null) => set({ user }),
  setToken: (token: string | null, persist = true) => {
    if (token) {
      if (persist) localStorage.setItem("token", token);
      else sessionStorage.setItem("token", token);
    } else {
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
    }
    set({ token });
  },
  setRefreshToken: (token: string | null) => {
    if (token) {
      localStorage.setItem("refreshToken", token);
    } else {
      localStorage.removeItem("refreshToken");
    }
    set({ refreshToken: token });
  },
  setLoading: (loading: boolean) => set({ loading }),
  setError: (error: string | null) => set({ error }),

  initializeAuth: async () => {
    const storedToken =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const storedRefreshToken = localStorage.getItem("refreshToken");

    if (storedToken) {
      // If the stored token is expired, check if we have a refresh token
      if (isTokenExpired(storedToken)) {
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        if (storedRefreshToken) {
          // Keep refresh token — the API interceptor will handle silent refresh
          set({
            token: null,
            user: null,
            refreshToken: storedRefreshToken,
            loading: false,
          });
        } else {
          // No refresh token — full logout
          set({
            token: null,
            user: null,
            refreshToken: null,
            loading: false,
          });
        }
        return;
      }

      set({ token: storedToken, loading: true });
      try {
        const decodedToken = decodeJwtToken(storedToken);
        set({
          user: {
            id: decodedToken?.id || "",
            email: decodedToken?.email || "",
            firstName: decodedToken?.firstName || "",
            lastName: decodedToken?.lastName || "",
            role: (decodedToken?.roles[0] as UserRole) || "",
            isVerified: decodedToken?.isVerified || false,
            authType:
              (decodedToken?.authType as
                | "manual"
                | "gmail"
                | "phone"
                | "google") || "manual",
            hasSetPassword: decodedToken?.hasSetPassword ?? true,
            preferences: {
              language: "en",
              currency: "USD",
              notifications: { email: true, sms: false, push: true },
              theme: "light",
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
        });
      } catch (err) {
        localStorage.removeItem("token");
        sessionStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        set({ token: null, user: null, refreshToken: null });
      } finally {
        set({ loading: false });
      }
    } else {
      set({ loading: false });
    }
  },

  hasRole: (role: string) => {
    const user = get().user;
    return (
      user?.role === role ||
      user?.role === UserRole[role as keyof typeof UserRole]
    );
  },

  logout: async () => {
    const refreshToken = get().refreshToken;
    if (refreshToken) {
      try {
        const { logout: logoutApi } = await import("../services/authService");
        await logoutApi(refreshToken);
      } catch {
        // Ignore errors — still clear local state
      }
    }
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    set({ user: null, token: null, refreshToken: null });
  },
}));
